"use client";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

type Variant = "fade-up" | "fade-in" | "slide-right" | "stagger" | "word-line" | "locations";

export function RevealGsap({
  children,
  as: As = "div",
  className = "",
  variant = "fade-up",
  delay = 0,
  duration = 0.85,
  start = "top 85%",
  style,
}: {
  children: React.ReactNode;
  as?: any;
  className?: string;
  variant?: Variant;
  delay?: number;
  duration?: number;
  start?: string;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = ref.current;
    if (!el) return;
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduce) {
      gsap.set(el, { opacity: 1, x: 0, y: 0 });
      const kids = el.querySelectorAll<HTMLElement>(":scope > *, .word-reveal-word");
      gsap.set(kids, { opacity: 1, x: 0, y: 0 });
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    // gsap.context() registriert ALLE in seinem Callback erstellten Tweens
    // und ScrollTriggers. ctx.revert() im Cleanup killt sie atomar — kein
    // einzelnes Vergessen mehr möglich, kein Leak in der Master-Timeline.
    const ctx = gsap.context(() => {
      const ease = "expo.out";

      // Initial-Sichtbarkeitsschutz: wenn Element schon im Viewport ist,
      // bevor ScrollTrigger refresht, könnte es dauerhaft opacity:0 bleiben.
      const rect = el.getBoundingClientRect();
      const isInitiallyVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (variant === "stagger") {
        const items = el.querySelectorAll<HTMLElement>(":scope > *");
        gsap.set(items, { opacity: 0, y: 32 });
        gsap.to(items, {
          opacity: 1, y: 0, duration, ease, stagger: 0.08, delay,
          immediateRender: false,
          scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
        });
      } else if (variant === "word-line") {
        const words = el.querySelectorAll<HTMLElement>(".word-reveal-word");
        gsap.set(words, { opacity: 0, y: 12, filter: "blur(3px)" });
        gsap.to(words, {
          opacity: 1, y: 0, filter: "blur(0px)", duration: 0.75, ease, stagger: 0.045, delay,
          immediateRender: false,
          scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
        });
      } else if (variant === "locations") {
        const items = el.querySelectorAll<HTMLElement>(":scope > *");
        gsap.set(items, { opacity: 0, y: 14, scale: 0.94 });
        gsap.to(items, {
          opacity: 1, y: 0, scale: 1, duration: 0.65, ease, stagger: 0.04, delay,
          immediateRender: false,
          scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
        });
      } else {
        const init = variant === "slide-right" ? { opacity: 0, x: 60 } : variant === "fade-in" ? { opacity: 0 } : { opacity: 0, y: 36 };
        gsap.set(el, init);
        gsap.to(el, {
          opacity: 1, x: 0, y: 0, duration, ease, delay,
          immediateRender: false,
          scrollTrigger: { trigger: el, start, toggleActions: "play none none none" },
        });
      }

      // Wenn das Element bereits in viewport ist, force-play nach
      // ScrollTrigger.refresh() — verhindert Dauer-Unsichtbarkeit.
      if (isInitiallyVisible) {
        ScrollTrigger.refresh();
      }
    }, ref);

    return () => {
      ctx.revert();
    };
  }, [variant, delay, duration, start]);

  const classes = ["reveal-gsap"];
  if (variant === "stagger") classes.push("reveal-stagger");
  else if (variant === "locations") classes.push("lp-locations-strip");
  else if (variant === "word-line") classes.push("word-reveal-line");
  return (
    <As ref={ref as any} className={`${classes.join(" ")} ${className}`.trim()} style={style}>
      {children}
    </As>
  );
}
